/* © 2017-2026 Booz Allen Hamilton Inc. All Rights Reserved. */

import React from 'react';
import PropTypes from 'prop-types';
import {
    Button,
    FauxImage,
    FauxUnavailableImage,
    toTitleCase,
    useFlags,
} from 'sarsaparilla';
import classnames from 'classnames';
import { formatPrice } from 'ui-tree-permit/src/utils';
import {
    EXPEDITED,
    EXPEDITED_PRICE,
    STANDARD,
    STANDARD_PRICE,
    PASS_IMG_PREFIX,
    RESERVATIONS_ACTION_UPDATE,
} from '../../constants';

export default function Summary({
    action,
    isSubmitting,
    modifierClasses,
    onSubmitHandler,
    passTypes,
    reservations,
    total,
}) {
    const hasReservations = !!reservations.length;
    const hasFooter = modifierClasses !== 'modal';
    const { iaPassesGovShutdown } = useFlags();
    const renderHeader = () => {
        let headerImage;

        if (passTypes.length === 1) {
            headerImage = `${PASS_IMG_PREFIX}${passTypes?.[0]?.img_url}`;
        } else {
            headerImage = `${PASS_IMG_PREFIX}${passTypes?.[1]?.img_url}`;
        }

        if (headerImage) {
            headerImage = (
                <FauxImage
                    src={headerImage}
                    altText="Annual Pass"
                    className="ia-summary-image"
                />
            );
        } else {
            headerImage = <FauxUnavailableImage className="ia-summary-image" />;
        }

        return <div className="ia-summary-header">{headerImage}</div>;
    };
    const renderReservationCards = () => {
        return (
            <div className="ia-reservation-cards">
                <div className="ia-reservation-cards-header">
                    <div className="ia-reservation-cards-title">
                        Interagency Annual Pass
                    </div>
                    <div className="ia-reservation-cards-description">
                        Interagency Pass
                    </div>
                </div>
                {reservations.map((item, key) => {
                    const quantity = item?.quantity;
                    const price = item?.pass_type?.cost * quantity;
                    const shipping =
                        item?.shipping?.Method === EXPEDITED ? EXPEDITED : STANDARD;
                    const shippingPrice =
                        item?.shipping?.Method === EXPEDITED
                            ? EXPEDITED_PRICE
                            : STANDARD_PRICE;

                    return (
                        <div
                            className="ia-reservation-card"
                            key={`ia-reservation-card-${key}`}
                        >
                            <div className="ia-reservation-card-body">
                                <div className="ia-summary-metadata">
                                    <div className="ia-summary-metadata-container">
                                        <div className="ia-summary-metadata-item">
                                            <div className="ia-summary-metadata-title">
                                                Start Date
                                            </div>
                                            <div className="ia-summary-metadata-description ia-summary-metadata-description-large">
                                                {item?.pass_type?.summary_start_date}
                                            </div>
                                        </div>
                                        <div className="ia-summary-metadata-item">
                                            <div className="ia-summary-metadata-title">
                                                Valid Through
                                            </div>
                                            <div className="ia-summary-metadata-description ia-summary-metadata-description-large">
                                                {item?.pass_type?.summary_end_date}
                                            </div>
                                        </div>
                                    </div>
                                </div>
                                <div className="ia-summary-metadata ia-summary-metadata-horizontal ia-summary-metadata-bg-gray">
                                    <div className="ia-summary-metadata-container">
                                        <div className="ia-summary-metadata-item">
                                            <div className="ia-summary-metadata-title">
                                                Interagency Annual Pass x{quantity}
                                            </div>
                                            <div className="ia-summary-metadata-description ia-summary-metadata-description-small">
                                                {formatPrice(price)}
                                            </div>
                                        </div>
                                    </div>
                                </div>
                                <div className="ia-summary-metadata ia-summary-metadata-horizontal">
                                    <div className="ia-summary-metadata-container">
                                        <div className="ia-summary-metadata-item">
                                            <div className="ia-summary-metadata-title">
                                                {toTitleCase(shipping)} Shipping
                                            </div>
                                            <div className="ia-summary-metadata-description">
                                                {formatPrice(shippingPrice)}
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </div>
                    );
                })}
            </div>
        );
    };
    const renderTotal = () => {
        return (
            <div className="ia-summary-total">
                <div className="ia-summary-total-title">Total Cost</div>
                <div className="ia-summary-total-description">{formatPrice(total)}</div>
            </div>
        );
    };
    const renderBody = () => {
        if (!hasReservations) {
            return (
                <div className="ia-summary-body">
                    <div className="ia-summary-message">
                        No passes have been selected.
                    </div>
                </div>
            );
        }

        return (
            <div className="ia-summary-body">
                {renderReservationCards()}
                {renderTotal()}
            </div>
        );
    };
    const renderFooter = () => {
        if (!hasFooter) {
            return null;
        }

        const ctaText =
            action === RESERVATIONS_ACTION_UPDATE ? 'Proceed to Cart' : 'Add to Cart';

        return (
            <div className="ia-summary-footer">
                <div className="ia-summary-controls">
                    <Button
                        className="ia-summary-cta"
                        isDisabled={!hasReservations || iaPassesGovShutdown}
                        isLoading={isSubmitting}
                        onClick={onSubmitHandler}
                    >
                        {ctaText}
                    </Button>
                </div>
            </div>
        );
    };

    return (
        <div
            className={classnames('ia-summary', {
                [`ia-summary-${modifierClasses}`]: modifierClasses,
            })}
        >
            {renderHeader()}
            {renderBody()}
            {renderFooter()}
        </div>
    );
}

Summary.propTypes = {
    action: PropTypes.string.isRequired,
    isSubmitting: PropTypes.bool.isRequired,
    modifierClasses: PropTypes.string,
    onSubmitHandler: PropTypes.func.isRequired,
    passTypes: PropTypes.array.isRequired,
    reservations: PropTypes.array.isRequired,
    total: PropTypes.number.isRequired,
};
